import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Info, ShieldCheck, IdCard } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import BrandMark from "@/components/BrandMark";
import { useClientTheme } from "@/contexts/ThemeContext";

const EMPTY = {
  name: "", surname: "", email: "", password: "",
  phone: "", document_number: "",
  address_street: "", address_city: "", address_zip: "",
  birth_date: "", birth_place: "",
  license_number: "",
};

const FIELDS = [
  { key: "name", labelIt: "Nome", labelEn: "First name", required: true, placeholder: "Mario" },
  { key: "surname", labelIt: "Cognome", labelEn: "Last name", required: true, placeholder: "Rossi" },
  { key: "document_number", labelIt: "N. Documento (CI/Passaporto)", labelEn: "Document number", required: true, placeholder: "CA1234567" },
  { key: "license_number", labelIt: "N. Patente", labelEn: "Driving licence", required: false, placeholder: "UI1234567" },
  { key: "birth_date", labelIt: "Data di nascita", labelEn: "Date of birth", required: true, type: "date" },
  { key: "birth_place", labelIt: "Luogo di nascita", labelEn: "Place of birth", required: true, placeholder: "Marciana Marina (LI)" },
  { key: "phone", labelIt: "Numero di telefono", labelEn: "Phone", required: true, type: "tel", placeholder: "+39 333 1234567" },
  { key: "email", labelIt: "Email", labelEn: "Email", required: true, type: "email", placeholder: "mario.rossi@email.com" },
  { key: "address_street", labelIt: "Via e numero civico", labelEn: "Street and number", required: true, placeholder: "Via Roma 12" },
  { key: "address_city", labelIt: "Città", labelEn: "City", required: true, placeholder: "Marciana Marina (LI)" },
  { key: "address_zip", labelIt: "CAP", labelEn: "ZIP code", required: true, placeholder: "57033" },
  { key: "password", labelIt: "Password (min 6 caratteri)", labelEn: "Password (min 6 chars)", required: true, type: "password" },
];

export default function Register() {
  const { t, i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const { register } = useAuth();
  const { theme } = useClientTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Iter 49 — honor ?returnTo=/scooter/:id?... when the user came from the public
  // catalog (they hit "Book" on a scooter → we sent them to /register).
  const rawReturnTo = searchParams.get("returnTo") || "";
  const returnTo = rawReturnTo.startsWith("/") ? rawReturnTo : "";
  const [form, setForm] = useState(EMPTY);
  const [accepted, setAccepted] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const missing = FIELDS.filter((f) => f.required && !String(form[f.key] || "").trim());

  const onSubmit = async (e) => {
    e.preventDefault();
    if (missing.length > 0) {
      toast.error(isEN ? `Missing fields: ${missing.map((f) => f.labelEn).join(", ")}` : `Campi mancanti: ${missing.map((f) => f.labelIt).join(", ")}`);
      return;
    }
    if (!accepted || !acceptedPrivacy) {
      toast.error(isEN ? "You must accept both Privacy and Contract" : "Devi accettare sia Privacy che Contratto");
      return;
    }
    setLoading(true);
    const res = await register({ ...form, accepted_contract: true, accepted_privacy: true });
    setLoading(false);
    if (res.ok) {
      toast.success(isEN ? "Registration completed" : "Registrazione completata");
      navigate(returnTo || "/home");
    } else {
      toast.error(res.error || (isEN ? "Registration failed" : "Errore registrazione"));
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-5 py-10" data-theme={theme} data-testid="register-root">
      <div className="absolute top-5 right-5 z-10 flex items-center gap-2">
        <ThemeSwitcher />
        <LanguageSwitcher />
      </div>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 text-center">
          <div className="inline-flex flex-col items-center gap-2 mb-3">
            <BrandMark size="lg" showWordmark={false} />
            <span className="font-display font-bold text-xl tracking-tight mt-1">Rent Lupi</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">{isEN ? "Create your account" : "Crea il tuo account"}</h1>
          <p className="text-zinc-400 mt-2 text-sm">
            {isEN ? "All fields marked with * are mandatory for the rental contract."
                  : "Tutti i campi con * sono obbligatori per la stipula del contratto di noleggio."}
          </p>
        </div>

        {/* Document pickup notice */}
        <div className="mb-5 rounded-xl border border-[#FFD700]/40 bg-[#FFD700]/5 p-4" data-testid="doc-pickup-notice">
          <div className="flex gap-3">
            <IdCard className="w-5 h-5 text-[#FFD700] shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-200 leading-relaxed">
              <span className="font-semibold text-[#FFD700]">{isEN ? "Important — Document pickup:" : "Importante — Ritiro documento:"}</span>{" "}
              {isEN
                ? "When picking up the vehicle at our office, the customer must present a valid identity document, which will be photocopied front and back for contractual and administrative purposes."
                : "Al momento del ritiro del mezzo presso la sede, il cliente dovrà presentare un documento di identità valido, che verrà copiato fronte e retro per finalità contrattuali e amministrative."}
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 sm:p-7 space-y-5" data-testid="register-form">
          <div className="grid sm:grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.key} className={f.key === "address_street" ? "sm:col-span-2" : ""}>
                <Label className="text-zinc-300 text-sm">
                  {(isEN ? f.labelEn : f.labelIt)} {f.required && <span className="text-[#FFD700]">*</span>}
                </Label>
                <Input
                  required={f.required}
                  type={f.type || "text"}
                  value={form[f.key]}
                  onChange={(e) => setField(f.key, e.target.value)}
                  placeholder={f.placeholder || ""}
                  className="bg-zinc-950 border-zinc-800 focus:ring-[#FFD700]/50 mt-1.5"
                  data-testid={`register-${f.key.replace(/_/g, "-")}-input`}
                />
              </div>
            ))}
          </div>

          {/* Contract acceptance */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-4">
            <div className="flex items-start gap-3">
              <Checkbox
                checked={accepted}
                onCheckedChange={setAccepted}
                id="accept-contract"
                className="mt-1 border-zinc-700 data-[state=checked]:bg-[#FFD700] data-[state=checked]:text-zinc-950 data-[state=checked]:border-[#FFD700]"
                data-testid="register-accept-contract"
              />
              <label htmlFor="accept-contract" className="text-sm text-zinc-200 leading-relaxed cursor-pointer select-none">
                <ShieldCheck className="w-4 h-4 inline mr-1 text-[#FFD700]" />
                {isEN ? (
                  <>I declare to have read, understood and fully accepted the{" "}
                    <Link to="/terms" target="_blank" className="text-[#FFD700] underline hover:brightness-110" data-testid="register-terms-link">
                      vehicle rental contract
                    </Link>{" "}of Rent Lupi.</>
                ) : (
                  <>Dichiaro di aver letto, compreso e accettato integralmente il{" "}
                    <Link to="/terms" target="_blank" className="text-[#FFD700] underline hover:brightness-110" data-testid="register-terms-link">
                      contratto di locazione veicoli
                    </Link>{" "}di Rent Lupi.</>
                )}{" "}
                <span className="text-[#FFD700]">*</span>
              </label>
            </div>
          </div>

          {/* Privacy acceptance */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-4">
            <div className="flex items-start gap-3">
              <Checkbox
                checked={acceptedPrivacy}
                onCheckedChange={setAcceptedPrivacy}
                id="accept-privacy"
                className="mt-1 border-zinc-700 data-[state=checked]:bg-[#FFD700] data-[state=checked]:text-zinc-950 data-[state=checked]:border-[#FFD700]"
                data-testid="register-accept-privacy"
              />
              <label htmlFor="accept-privacy" className="text-sm text-zinc-200 leading-relaxed cursor-pointer select-none">
                <ShieldCheck className="w-4 h-4 inline mr-1 text-[#FFD700]" />
                {isEN ? (
                  <>I have read and accept the{" "}
                    <Link to="/privacy-policy" target="_blank" className="text-[#FFD700] underline hover:brightness-110" data-testid="register-privacy-link">
                      Privacy &amp; Cookie Policy
                    </Link>{" "}(GDPR — EU Reg. 2016/679). My data will be used solely for the rental activity, no profiling, no third-party sharing.</>
                ) : (
                  <>Ho letto e accetto la{" "}
                    <Link to="/privacy-policy" target="_blank" className="text-[#FFD700] underline hover:brightness-110" data-testid="register-privacy-link">
                      Privacy e Cookie Policy
                    </Link>{" "}(GDPR — Reg. UE 2016/679). I miei dati saranno usati esclusivamente per l&apos;attività di noleggio, nessuna profilazione, nessuna cessione a terzi.</>
                )}{" "}
                <span className="text-[#FFD700]">*</span>
              </label>
            </div>
          </div>

          <div className="flex items-start gap-2 text-xs text-zinc-500">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{isEN ? "Your data is treated according to our Privacy & Cookie Policy."
                      : "I tuoi dati sono trattati secondo la nostra Privacy e Cookie Policy."}</span>
          </div>

          <Button type="submit" disabled={loading || !accepted || !acceptedPrivacy || missing.length > 0}
                  className="w-full bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold disabled:opacity-40"
                  data-testid="register-submit-button">
            {loading ? (isEN ? "Submitting…" : "Invio in corso…") : (isEN ? "Create account" : "Crea account")}
          </Button>

          <div className="text-center text-sm text-zinc-400">
            {isEN ? "Already have an account?" : "Hai già un account?"}{" "}
            <Link to="/login" className="text-[#FFD700] hover:underline" data-testid="register-go-to-login">
              {isEN ? "Sign in" : "Accedi"}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
